import React, { useEffect, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { formatClubDateTime } from '../../utils/clubTime';
import CheckLengthBadge, { needsLengthCheck } from './CheckLengthBadge';
import ExcludedLengthBadge from './ExcludedLengthBadge';
import { describeFlag, flagCodes, isReviewed, nextRotation, AI_FLAG_SCORE_THRESHOLD, type ReviewCatch, checkedByLabel } from '@shared/adjudication';
import { catchRotation, rotatedFrameStyle, rotatedImageStyle } from '../../utils/catchRotation';
import { getCatchCardStatus } from './CatchStatusLegend';

const client = generateClient({ authMode: 'userPool' });

const SPECIES_LABELS: Record<string, string> = {
  FLATHEAD: 'Flathead',
  BREAM: 'Bream',
  JEWFISH: 'Jewfish',
  MANGROVE_JACK: 'Mangrove Jack',
  WHITING: 'Whiting',
  TAILOR: 'Tailor',
  TREVALLY: 'Trevally',
  OTHER: 'Other',
};

interface CatchDetailDialogProps {
  catchItem: ReviewCatch & {
    species: string;
    lengthCm: number;
    competitorName?: string | null;
    timeOfCatch?: string | null;
    submissionTimestamp?: string | null;
    photoS3Key?: string | null;
    gpsVerified?: boolean | null;
    displayRotation?: number | null;
  };
  rank?: number | null;
  /** Already-resolved photo URL (e.g. SubmissionReview's thumbnail cache). If omitted, loaded here via generateDownloadUrl. */
  photoUrl?: string | null;
  onApprove: (catchId: string) => void | Promise<void>;
  onDisqualify: (catchId: string, reason: string) => void | Promise<void>;
  /** A4: moves a DISQUALIFIED catch back to APPROVED via REINSTATE_CATCH, instead of Approve. */
  onReinstate?: (catchId: string) => void | Promise<void>;
  actionLoading?: boolean;
  onClose: () => void;
  /** Called after a successful rotate, so the caller's cards refresh (e.g. onRefresh). */
  onRotated?: (catchId: string, rotation: 0 | 90 | 180 | 270) => void | Promise<void>;
  /** Whether the competition is published, if the caller knows. Rotate is disabled with an
   * explanation when true; otherwise the control is left enabled and the server's own refusal
   * message is shown if it rejects the rotation. */
  competitionPublished?: boolean;
}

/**
 * Full catch detail view (#144 R3). Modal with a focus trap, Escape and a
 * visible Close button both close it, and focus returns to the opener
 * (handled by the caller re-focusing the triggering element after onClose).
 */
const CatchDetailDialog: React.FC<CatchDetailDialogProps> = ({
  catchItem,
  rank = null,
  photoUrl: photoUrlProp,
  onApprove,
  onDisqualify,
  onReinstate,
  actionLoading = false,
  onClose,
  onRotated,
  competitionPublished = false,
}) => {
  const [photoUrl, setPhotoUrl] = useState<string | null>(photoUrlProp ?? null);
  const [zoomed, setZoomed] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [rotation, setRotation] = useState<0 | 90 | 180 | 270>(catchRotation(catchItem));
  const [rotating, setRotating] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [rotateAnnouncement, setRotateAnnouncement] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setRotation(catchRotation(catchItem));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catchItem.id, catchItem.displayRotation]);

  useEffect(() => {
    if (photoUrlProp !== undefined) {
      setPhotoUrl(photoUrlProp ?? null);
      return;
    }
    if (!catchItem.photoS3Key) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await (client.queries as any).generateDownloadUrl({ s3Key: catchItem.photoS3Key });
        const data = parseLambdaResponse<{ downloadUrl?: string }>(result?.data);
        if (!cancelled && data?.downloadUrl) setPhotoUrl(data.downloadUrl);
      } catch { /* skip */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catchItem.id, catchItem.photoS3Key, photoUrlProp]);

  // Focus the Close button on open, trap Tab within the dialog, close on Escape.
  useEffect(() => {
    closeButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const container = dialogRef.current;
      if (!container) return;
      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const speciesLabel = SPECIES_LABELS[catchItem.species] || catchItem.species;
  const status = getCatchCardStatus(catchItem, rank);
  const codes = flagCodes(catchItem.aiFlags);
  const score = typeof catchItem.aiSuspicionScore === 'number' ? catchItem.aiSuspicionScore : null;
  const scoreFlagged = score !== null && score >= AI_FLAG_SCORE_THRESHOLD;
  const checkedLabel = isReviewed(catchItem)
    ? checkedByLabel(catchItem as any, formatClubDateTime)
    : 'Not checked';
  const catchTime = catchItem.timeOfCatch || catchItem.submissionTimestamp;
  const isDisqualifiedCatch = catchItem.status === 'DISQUALIFIED';
  const canDisqualify = !isDisqualifiedCatch;
  const canApprove = !isDisqualifiedCatch && !isReviewed(catchItem);
  const canReinstate = isDisqualifiedCatch;

  const handleDisqualifyClick = async () => {
    if (!reason.trim()) {
      setReasonError('A reason is required to disqualify a catch.');
      return;
    }
    setReasonError(null);
    await onDisqualify(catchItem.id, reason.trim());
  };

  const handleRotate = async () => {
    const previous = rotation;
    const next = nextRotation(previous);
    setRotateError(null);
    setRotating(true);
    // Optimistic display: show the new rotation immediately, revert on failure.
    setRotation(next);
    try {
      const result = await (client.mutations as any).adjudicateCatch({
        actionType: 'SET_CATCH_ROTATION',
        catchId: catchItem.id,
        rotation: next,
      });
      if (result?.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }
      const data = parseLambdaResponse<{ success?: boolean; rotation?: number; error?: string }>(result?.data);
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to rotate the photo');
      }
      setRotateAnnouncement(`Photo rotated to ${next} degrees`);
      await onRotated?.(catchItem.id, next);
    } catch (err: any) {
      setRotation(previous);
      setRotateError(err?.message || 'Failed to rotate the photo');
    } finally {
      setRotating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-2 sm:p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="catch-detail-title"
          className="relative w-full max-w-lg rounded-lg bg-white p-4 shadow-xl sm:p-6"
        >
          <div className="flex items-start justify-between gap-3">
            <h2 id="catch-detail-title" className="text-lg font-bold text-gray-900">
              {speciesLabel}, {catchItem.lengthCm}cm, {catchItem.competitorName || 'Unknown'}
            </h2>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="min-h-[44px] min-w-[44px] shrink-0 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          {/* Photo */}
          <div className="mt-3">
            {photoUrl ? (
              <button
                type="button"
                onClick={() => setZoomed((z) => !z)}
                className="block w-full overflow-hidden rounded-md bg-gray-100"
                style={{ maxHeight: '70vh', height: (rotation === 90 || rotation === 270) ? '70vh' : undefined, ...rotatedFrameStyle(rotation) }}
                aria-label={zoomed ? 'Zoom out' : 'Zoom in'}
              >
                <img
                  src={photoUrl}
                  alt={`Catch photo: ${speciesLabel}, ${catchItem.lengthCm} cm`}
                  className="mx-auto block w-full object-contain transition-transform"
                  style={{
                    maxHeight: (rotation === 90 || rotation === 270) ? undefined : '70vh',
                    transform: zoomed ? 'scale(2)' : 'scale(1)',
                    ...rotatedImageStyle(rotation),
                  }}
                />
              </button>
            ) : (
              <div className="flex h-40 w-full items-center justify-center rounded-md bg-gray-100 text-sm text-gray-400">
                No photo
              </div>
            )}
            {photoUrl && (
              <a
                href={photoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-block text-sm text-fishing-blue hover:underline"
              >
                Open full size
              </a>
            )}
            {/* Rotate control (#144 R7). The stored image is never changed: this
                only sets displayRotation, applied wherever the photo is shown. */}
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={handleRotate}
                disabled={rotating || competitionPublished}
                aria-label="Rotate photo 90 degrees clockwise"
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
              >
                <span aria-hidden="true">⟳</span>
                Rotate
              </button>
              {competitionPublished && (
                <span className="text-xs text-gray-500">Results are published. Unpublish first to change a photo.</span>
              )}
            </div>
            {rotateError && (
              <p role="alert" className="mt-1 text-sm text-red-700">{rotateError}</p>
            )}
            <div aria-live="polite" className="sr-only">{rotateAnnouncement}</div>
          </div>

          {/* Details */}
          <dl className="mt-4 grid grid-cols-2 gap-2 text-sm text-gray-700">
            <div>
              <dt className="font-medium text-gray-500">Species</dt>
              <dd>{speciesLabel}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Length</dt>
              <dd className="flex items-center gap-1">
                {catchItem.lengthCm}cm
                <CheckLengthBadge catchItem={catchItem} />
                <ExcludedLengthBadge catchItem={catchItem} />
              </dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Competitor</dt>
              <dd>{catchItem.competitorName || 'Unknown'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Time of catch</dt>
              <dd>{catchTime ? formatClubDateTime(catchTime) : 'Unknown'}</dd>
            </div>
          </dl>

          {needsLengthCheck(catchItem) && (
            <p className="mt-2 text-sm font-medium text-orange-800">This length is unusual for the species. Check the photo.</p>
          )}

          {catchItem.gpsVerified === false && (
            <p className="mt-1 text-xs text-gray-500">GPS unverified</p>
          )}

          {/* Status */}
          <p className={`mt-3 text-sm font-semibold ${
            status.state === 'disqualified' ? 'text-red-700'
            : status.state === 'needsReview' ? 'text-amber-700'
            : status.state === 'checked' ? 'text-green-700'
            : 'text-gray-600'
          }`}>
            {status.label}
          </p>

          {/* AI flags */}
          <div className="mt-3 rounded-md bg-gray-50 p-3">
            <p className="text-sm font-semibold text-gray-900">
              AI score: {score ?? 'Not available'}
              {score !== null && (
                <span className={`ml-2 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${scoreFlagged ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                  {scoreFlagged ? 'Flagged' : 'Not flagged'}
                </span>
              )}
            </p>
            <p className="text-xs text-gray-500">
              0 to 100, higher is more suspicious. Flagged at {AI_FLAG_SCORE_THRESHOLD} or more. Advisory only: the admin makes all final decisions.
            </p>
            {codes.length > 0 ? (
              <ul className="mt-2 space-y-1 text-sm text-gray-700">
                {codes.map((code) => (
                  <li key={code} className="flex items-start gap-1">
                    <span aria-hidden="true" className="text-red-500">⚑</span>
                    <span>{describeFlag(code)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-gray-500">No AI flags.</p>
            )}
          </div>

          {/* Checked-by line */}
          <p className="mt-3 text-sm text-gray-700">{checkedLabel}</p>

          {/* Actions */}
          <div className="mt-4 space-y-2">
            {canApprove && (
              <button
                type="button"
                onClick={() => onApprove(catchItem.id)}
                disabled={actionLoading}
                className="min-h-[44px] w-full rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:bg-gray-300"
              >
                {actionLoading ? 'Working…' : 'Approve'}
              </button>
            )}
            {canReinstate && (
              <button
                type="button"
                onClick={() => onReinstate?.(catchItem.id)}
                disabled={actionLoading}
                className="min-h-[44px] w-full rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:bg-gray-300"
              >
                {actionLoading ? 'Working…' : 'Reinstate'}
              </button>
            )}
            {canDisqualify && (
              <div>
                <label htmlFor="catch-detail-reason" className="block text-sm font-medium text-gray-700">
                  Reason for disqualifying
                </label>
                <textarea
                  id="catch-detail-reason"
                  value={reason}
                  onChange={(e) => { setReason(e.target.value); if (reasonError) setReasonError(null); }}
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  rows={2}
                />
                {reasonError && <p role="alert" className="mt-1 text-sm text-red-700">{reasonError}</p>}
                <button
                  type="button"
                  onClick={handleDisqualifyClick}
                  disabled={actionLoading}
                  className="mt-2 min-h-[44px] w-full rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:bg-gray-300"
                >
                  {actionLoading ? 'Working…' : 'Disqualify'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatchDetailDialog;
